
import React, { useEffect, Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { PresenceProvider } from './contexts/PresenceContext';
import { MainLayout } from './components/layout/MainLayout';
import { UserRole } from './types';
import { Card, CardContent } from './components/ui/Card';
import { Button } from './components/ui/Button';
import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { FlashAlertPopup } from './components/dashboard/FlashAlertPopup';
import { clearGeneratedDaysCache, autoGenerateDailyRituals, getFilteredTaskInstances, cleanupFuturePreGeneratedRituals } from './services/api';

// Lazy-loaded page components for fast initial bundle and instant loading
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ClientsPage = lazy(() => import('./pages/ClientsPage').then(m => ({ default: m.ClientsPage })));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage').then(m => ({ default: m.ClientDetailPage })));
const TasksPage = lazy(() => import('./pages/TasksPage').then(m => ({ default: m.TasksPage })));
const CalendarPage = lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage').then(m => ({ default: m.PaymentsPage })));
const DiaryPage = lazy(() => import('./pages/DiaryPage').then(m => ({ default: m.DiaryPage })));
const RulesPage = lazy(() => import('./pages/RulesPage').then(m => ({ default: m.RulesPage })));
const ArchivePage = lazy(() => import('./pages/ArchivePage').then(m => ({ default: m.ArchivePage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const MediaPage = lazy(() => import('./pages/MediaPage').then(m => ({ default: m.MediaPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const MessengerPage = lazy(() => import('./pages/MessengerPage').then(m => ({ default: m.MessengerPage })));
const PlanningPage = lazy(() => import('./pages/PlanningPage').then(m => ({ default: m.PlanningPage })));
const ControlPage = lazy(() => import('./pages/ControlPage').then(m => ({ default: m.ControlPage })));
const DominaStructurePage = lazy(() => import('./pages/DominaStructurePage').then(m => ({ default: m.DominaStructurePage })));
const TimerPage = lazy(() => import('./pages/TimerPage').then(m => ({ default: m.TimerPage })));
const AudioInputsPage = lazy(() => import('./pages/AudioInputsPage').then(m => ({ default: m.AudioInputsPage })));
const CoachingPage = lazy(() => import('./pages/CoachingPage').then(m => ({ default: m.CoachingPage })));

const PageLoadingFallback: React.FC = () => (
  <div className="flex-1 flex flex-col items-center justify-center min-h-[50vh] p-8 animate-fade-in">
    <div className="w-10 h-10 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center animate-pulse shadow-glow">
      <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
    <span className="mt-3 text-xs text-text-secondary tracking-widest uppercase font-medium">Wird geladen...</span>
  </div>
);

// Sub-Komponente für Domina-Routen
const DominaRoutes = () => (
    <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
            <Route path="/" element={<MainLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="clients/:clientId" element={<ClientDetailPage />} />
                <Route path="coaching" element={<CoachingPage />} />
                <Route path="structure" element={<DominaStructurePage />} />
                <Route path="control" element={<ControlPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="diary" element={<DiaryPage />} />
                <Route path="rules" element={<RulesPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="check-in" element={<ReportsPage />} />
                <Route path="media" element={<MediaPage />} />
                <Route path="audio-inputs" element={<AudioInputsPage />} />
                <Route path="messenger" element={<MessengerPage />} />
                <Route path="timer" element={<TimerPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/" />} />
            </Route>
        </Routes>
    </Suspense>
);

// Sub-Komponente für Sub-Routen
const SubRoutes = ({ clientId }: { clientId: string }) => (
    <>
        <FlashAlertPopup clientId={clientId} />
        <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
                <Route path="/" element={<MainLayout />}>
                    <Route index element={<DashboardPage />} />
                    <Route path="coaching" element={<CoachingPage />} />
                    <Route path="planning" element={<PlanningPage />} />
                    <Route path="control" element={<ControlPage />} />
                    <Route path="tasks" element={<TasksPage />} />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route path="diary" element={<DiaryPage />} />
                    <Route path="rules" element={<RulesPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="check-in" element={<ReportsPage />} />
                    <Route path="media" element={<MediaPage />} />
                    <Route path="audio-inputs" element={<AudioInputsPage />} />
                    <Route path="archive" element={<ArchivePage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="messenger" element={<MessengerPage />} />
                    <Route path="timer" element={<TimerPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/" />} />
                </Route>
            </Routes>
        </Suspense>
    </>
);

const AppRoutes: React.FC = () => {
    const { isAuthenticated, user, logout, loading } = useAuth();
    
    console.log("AppRoutes: Rendering", { isAuthenticated, hasUser: !!user, loading });

    if (!isAuthenticated) {
        // Save current target path from hash or search if deep-linked from Telegram
        try {
            const rawHash = window.location.hash ? window.location.hash.replace(/^#/, '') : '';
            if (rawHash && !rawHash.startsWith('/login')) {
                sessionStorage.setItem('fairyrealm_redirect_after_login', rawHash);
            }
        } catch {
            // Ignore storage access errors
        }

        return (
            <Suspense fallback={<PageLoadingFallback />}>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="*" element={<Navigate to="/login" />} />
                </Routes>
            </Suspense>
        );
    }

    if (!user) {
        return (
            <div className="h-screen w-screen flex items-center justify-center bg-background p-4">
                <Card className="border-red-500/30">
                    <CardContent className="p-8 text-center">
                        <h2 className="text-xl font-bold text-red-400 font-heading">Profil fehlt</h2>
                        <p className="text-text-secondary mt-2 text-sm italic">
                            Dein Account wurde noch nicht vollständig initialisiert.
                        </p>
                        <Button onClick={() => logout()} variant="secondary" className="mt-6">
                            Abmelden
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }
    
    return user.role === UserRole.DOMINA ? <DominaRoutes /> : <SubRoutes clientId={user.clientId || user.id} />;
};

import { ErrorBoundary } from './components/ErrorBoundary';

// Stability booster: Handles visibility changes, midnight day rollover, and ensures session health globally
const AppManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { refresh, isAuthenticated, user } = useAuth();
    
    // 1. Midnight rollover watcher: detects when day changes at midnight (00:00) and triggers ritual generation
    useEffect(() => {
        if (!isAuthenticated) return;

        const getTodayStr = () => {
            const d = new Date();
            const yr = d.getFullYear();
            const mo = String(d.getMonth() + 1).padStart(2, '0');
            const da = String(d.getDate()).padStart(2, '0');
            return `${yr}-${mo}-${da}`;
        };

        let lastCheckedDate = getTodayStr();

        const handleMidnightTransition = async (forceCheck = false) => {
            const currentDate = getTodayStr();
            if (currentDate !== lastCheckedDate || forceCheck) {
                console.log(`[Client Midnight Watcher] Date changed from ${lastCheckedDate} to ${currentDate}. Generating today's rituals...`);
                lastCheckedDate = currentDate;
                clearGeneratedDaysCache();
                await cleanupFuturePreGeneratedRituals().catch(() => {});
                
                if (user?.role === UserRole.DOMINA) {
                    await getFilteredTaskInstances({ isDominaView: true, clientId: 'all' }).catch(() => {});
                } else if (user?.clientId || user?.id) {
                    await autoGenerateDailyRituals(user.clientId || user.id, currentDate).catch(() => {});
                }
            }
        };

        // Check every 30 seconds for date change (handles device wake, tab focus, or midnight tick)
        const intervalId = setInterval(() => {
            handleMidnightTransition(false);
        }, 30000);

        // Precise midnight timeout
        let timerId: ReturnType<typeof setTimeout> | null = null;
        const scheduleMidnightTimeout = () => {
            const now = new Date();
            const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 2);
            const msUntilMidnight = Math.max(nextMidnight.getTime() - now.getTime(), 1000);

            timerId = setTimeout(async () => {
                await handleMidnightTransition(true);
                scheduleMidnightTimeout();
            }, msUntilMidnight);
        };
        scheduleMidnightTimeout();

        return () => {
            clearInterval(intervalId);
            if (timerId) clearTimeout(timerId);
        };
    }, [isAuthenticated, user]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isAuthenticated) {
                console.log("AppManager: App became visible, refreshing session/data...");
                // Gentle refresh of auth session and user role
                refresh().catch(err => console.warn("AppManager: Refresh failed", err));
            }
        };

        const handleFocus = () => {
            // Also refresh on window focus
            if (isAuthenticated) {
                refresh().catch(() => {});
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [refresh, isAuthenticated]);

    return <>{children}</>;
};

const App: React.FC = () => {
  console.log("App component rendering");
  return (
    <ErrorBoundary>
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <AuthProvider>
            <AppManager>
                <NotificationsProvider>
                  <PresenceProvider>
                    <UnreadMessagesProvider>
                          <AppRoutes />
                    </UnreadMessagesProvider>
                  </PresenceProvider>
                </NotificationsProvider>
            </AppManager>
          </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
